/**
 * Profile service — read/update user profile documents.
 */
import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  arrayUnion,
  increment,
  Timestamp,
} from "firebase/firestore";

import { db } from "../lib/firebase";
import type { League, SkillLevel, UserProfile, SubscriptionTier } from "../types";

/**
 * Return every league the user belongs to. Backwards-compat: legacy
 * `profile.league` (single value) is wrapped into an array. New writes
 * populate `profile.leagues` directly.
 *
 * Users can now belong to BOTH Entry and Primary at the same time —
 * pick both checkboxes in profile edit. They then appear on both
 * leaderboards (each still gated by having matchCount > 0 in that
 * league). Was a hard-single-select before; users who wanted to
 * compete in both had to keep flipping.
 */
export function getUserLeagues(
  profile:
    | { league?: League; leagues?: League[] | null }
    | null
    | undefined,
): League[] {
  if (!profile) return [];
  if (Array.isArray(profile.leagues)) {
    return profile.leagues.filter(
      (l): l is League => l === "entry" || l === "primary",
    );
  }
  if (profile.league === "primary" || profile.league === "entry") {
    return [profile.league];
  }
  return [];
}

/** True if the user belongs to `league`. */
export function isInLeague(
  profile:
    | { league?: League; leagues?: League[] | null }
    | null
    | undefined,
  league: League,
): boolean {
  return getUserLeagues(profile).includes(league);
}

/**
 * Legacy single-league accessor — returns the FIRST league the user
 * belongs to. Kept for existing callers that show a single badge.
 * New code should prefer getUserLeagues() to handle multi-league users.
 */
export function getUserLeague(
  profile:
    | { league?: League; leagues?: League[] | null }
    | null
    | undefined,
): League | undefined {
  return getUserLeagues(profile)[0];
}

const PROFILES = "profiles";

/**
 * BATCH read for many profiles at once. Firestore's `where(documentId,
 * "in", [...])` supports up to 30 IDs per query — we chunk if more.
 * Skips the tier migration / points-claim side effects that `getProfile`
 * does for the signed-in user, so this is safe for display-only lists.
 *
 * Missing profiles simply don't appear in the result — the caller should
 * not assume order matches the input.
 */
export async function getManyProfilesLite(
  uids: string[],
): Promise<UserProfile[]> {
  if (uids.length === 0) return [];
  const { collection, query, where, getDocs, documentId } = await import(
    "firebase/firestore"
  );
  const results: UserProfile[] = [];
  // Chunk into 30-at-a-time to satisfy Firestore's `in` limit.
  for (let i = 0; i < uids.length; i += 30) {
    const chunk = uids.slice(i, i + 30);
    const q = query(
      collection(db, PROFILES),
      where(documentId(), "in", chunk),
    );
    const snap = await getDocs(q);
    for (const d of snap.docs) {
      results.push({ id: d.id, ...(d.data() as any) });
    }
  }
  return results;
}

export async function getProfile(userId: string): Promise<UserProfile | null> {
  console.log("[GETPROFILE] called for", userId);
  // MIGRATE_LEGACY_TIER removed 2026-08-06 — new accounts default to
  // tier=free (getEffectiveTier returns 'free' when the field is
  // missing). Existing trial users keep their auto-granted tier=elite
  // + tierExpiresAt until it naturally lapses; nothing here touches
  // those docs so no disruption to their remaining trial.
  const ref = doc(db, "profiles", userId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const data = snap.data() as UserProfile;
  // Claim any pending tournament points (fire-and-forget)
  claimPendingTournamentPoints(userId).catch(() => undefined);
  return data;
}

export async function updateProfile(
  userId: string,
  patch: Partial<Omit<UserProfile, "id" | "createdAt">>,
): Promise<void> {
  // Mirror displayName → displayNameLower so search keeps working after edits.
  const finalPatch: Record<string, unknown> = { ...patch, updatedAt: serverTimestamp() };
  if (typeof patch.displayName === "string") {
    finalPatch.displayNameLower = patch.displayName.trim().toLowerCase();
  }
  if (typeof patch.city === "string") {
    finalPatch.cityLower = patch.city.trim().toLowerCase();
  }
  // Firestore rejects `undefined` values — strip them out. Callers often
  // pass `ntrpRating: profile?.ntrpRating` where the source is undefined
  // for legacy accounts, which was crashing the update.
  const cleaned = Object.fromEntries(
    Object.entries(finalPatch).filter(([, v]) => v !== undefined),
  );
  await updateDoc(doc(db, PROFILES, userId), cleaned);
}

/**
 * Atomically add Acework points to a user's level bucket and mark the source
 * match as claimed. Idempotent at the call-site via the matchId guard.
 */
export async function addPointsToLevel(
  userId: string,
  level: SkillLevel,
  points: number,
  matchId: string,
): Promise<void> {
  await updateDoc(doc(db, PROFILES, userId), {
    [`acework_points.${level}`]: increment(points),
    pointsClaimedMatchIds: arrayUnion(matchId),
    updatedAt: serverTimestamp(),
  });
}

// ---------- Trial / tier helpers ----------

import type { Timestamp as FsTimestamp } from "firebase/firestore";

type TierLike = {
  tier?: SubscriptionTier;
  tierExpiresAt?: FsTimestamp;
  /** Permanent lifetime-free "Founding Member" flag — see UserProfile. */
  foundingMember?: boolean;
};

/**
 * The user's *current* tier — drops to "free" once tierExpiresAt has passed.
 * EXCEPTION: Founding Members are ALWAYS "elite" (permanent lifetime-free
 * membership granted by admin to the launch cohort). Their tag never expires.
 */
export function getEffectiveTier(profile: TierLike | null | undefined): SubscriptionTier {
  if (profile?.foundingMember === true) return "elite"; // NEVER expires
  if (!profile?.tier) return "free";
  if (profile.tierExpiresAt) {
    const ms = (profile.tierExpiresAt as FsTimestamp).toMillis?.() ?? 0;
    if (ms > 0 && Date.now() >= ms) return "free";
  }
  return profile.tier;
}

/**
 * Days remaining on the trial / paid plan. null if no expiry set. 0 if expired.
 * Founding Members return null — they have no expiry.
 */
export function getTrialDaysRemaining(profile: TierLike | null | undefined): number | null {
  if (profile?.foundingMember === true) return null;
  if (!profile?.tierExpiresAt) return null;
  const ms = (profile.tierExpiresAt as FsTimestamp).toMillis?.() ?? 0;
  const remain = ms - Date.now();
  if (remain <= 0) return 0;
  return Math.ceil(remain / (24 * 60 * 60 * 1000));
}

/**
 * True if user is on the auto-granted 90-day signup trial (vs paid).
 * Founding Members return false — they're a permanent grant, not a trial.
 */
export function isOnTrial(profile: TierLike | null | undefined): boolean {
  if (profile?.foundingMember === true) return false;
  if (!profile?.tier || profile.tier === "free") return false;
  if (!profile.tierExpiresAt) return false;
  const ms = (profile.tierExpiresAt as FsTimestamp).toMillis?.() ?? 0;
  return ms > Date.now();
}

/**
 * True if the profile is an active member — founding OR paid Acer whose
 * subscription hasn't lapsed. Free users AND expired trial users return
 * false. Used to gate paid features:
 *   - Ace Buzz accrual
 *   - Tournament 24h early-access window
 *   - Leaderboard eligibility (future)
 *
 * NOTE: existing trial users (accounts that got the auto-granted 90-day
 * Elite trial before it was removed) will pass this check until their
 * tierExpiresAt lapses — intentional, we honor what was already given.
 */
export function isMember(profile: TierLike | null | undefined): boolean {
  if (!profile) return false;
  if (profile.foundingMember === true) return true;
  return getEffectiveTier(profile) !== "free";
}

/**
 * Scan tournaments where this user participated + status="completed" +
 * has pointsByUid for them. For each one not yet claimed, increment
 * acework_points at user's current NTRP level and mark claimed.
 * Idempotent — safe to call on every profile load.
 */
export async function claimPendingTournamentPoints(uid: string): Promise<number> {
  try {
    const { collection: col, query: q, where, getDocs, arrayUnion: aU, increment: inc } = await import("firebase/firestore");
    const { ntrpToLevel } = await import("./points");

    const profileSnap = await getDoc(doc(db, "profiles", uid));
    if (!profileSnap.exists()) return 0;
    const profile = profileSnap.data() as any;
    const claimed = new Set<string>(profile.pointsClaimedTournamentIds ?? []);
    const level = ntrpToLevel(profile.ntrpRating ?? 3.0);

    const tSnap = await getDocs(
      q(
        col(db, "tournaments"),
        where("participantIds", "array-contains", uid),
        where("status", "==", "completed"),
      )
    );

    let total = 0;
    for (const tDoc of tSnap.docs) {
      if (claimed.has(tDoc.id)) continue;
      const t = tDoc.data() as any;
      const points = t.pointsByUid?.[uid] ?? 0;
      if (points <= 0) continue;

      await updateDoc(doc(db, "profiles", uid), {
        [`acework_points.${level}`]: inc(points),
        pointsClaimedTournamentIds: aU(tDoc.id),
      });
      total += points;
      console.log("[CLAIM_POINTS] claimed", points, "from tournament", tDoc.id, "at level", level);
    }
    return total;
  } catch (e) {
    console.error("[CLAIM_POINTS] failed:", e);
    return 0;
  }
}

/** Look up a profile by handle (without @ prefix). */
export async function getProfileByHandle(handle: string): Promise<UserProfile | null> {
  const { collection, query, where, getDocs, limit } = await import("firebase/firestore");
  const q = query(collection(db, PROFILES), where("handle", "==", handle.toLowerCase()), limit(1));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { ...(d.data() as UserProfile), uid: d.id };
}
